// EffectLedger. Tracks active effects with their expiry. Pure data, no
// rendering, no React. Designed to be cheap to call from
// recalculateSiteAffinities on every site change and turn pass.

import { OP } from './schema';
import { matchesFilter } from './cardFilter';

// Cost-modifier filter check. Thin wrapper over the canonical
// `matchesFilter` primitive. The cost-mod context is just "candidate
// card + the cell the cast is landing on." All field semantics
// (type, subtype, name_words, cells) live in `cardFilter.js`.
function matchesCostFilter(filter, candidate, destinationCell = null) {
  return matchesFilter(candidate, filter, { destinationCell });
}

let _idCounter = 0;
function makeId() {
  _idCounter += 1;
  return `eff-${Date.now().toString(36)}-${_idCounter}`;
}

export class EffectLedger {
  constructor() {
    this.entries = [];
  }

  add(entry) {
    const id = makeId();
    this.entries.push({ id, ...entry });
    return id;
  }

  removeBySource(sourceCardId) {
    this.entries = this.entries.filter((e) => e.sourceCardId !== sourceCardId);
  }

  // Remove a single entry by its generated id. Used by the HUD buff
  // panel when the user clicks ✕ on one specific row. Distinct from
  // removeBySource which would wipe every entry from one card.
  // Idempotent: removing a non-existent id is a no-op.
  removeById(entryId) {
    this.entries = this.entries.filter((e) => e.id !== entryId);
  }

  // Sweep expired entries. Called on turn pass with the player whose turn
  // just ended. An entry expires when its expiry.player matches AND its
  // expiry.turnNumber <= the player's just-ended turn number.
  pruneExpired({ player, turnNumber }) {
    this.entries = this.entries.filter((e) => {
      if (e.expiry.type === 'permanent') return true;
      if (e.expiry.type === 'until_leaves') return true; // removed via removeBySource
      if (e.expiry.type === 'turn_end') {
        const matchesPlayer = e.expiry.player === player;
        const hasExpired = e.expiry.turnNumber <= turnNumber;
        return !(matchesPlayer && hasExpired);
      }
      return true;
    });
  }

  thresholdContribution(player) {
    const totals = { air: 0, earth: 0, fire: 0, water: 0 };
    for (const entry of this.entries) {
      if (entry.controller !== player) continue;
      if (entry.effect.op !== OP.GRANT_THRESHOLD) continue;
      for (const [k, v] of Object.entries(entry.effect.args)) {
        if (k in totals) totals[k] += v;
      }
    }
    return totals;
  }

  // v1.89 / v1.99. Flat cost-modifier total for spells cast by
  // `player`. Entries with op MODIFY_CAST_COST are owned by the
  // controller; entry.targetPlayer is the player whose costs are
  // affected. v1.99: optional `candidateCard` filters modifiers whose
  // args.filter doesn't match the card being cast (Templar pattern:
  // only Knight/Sir/Dame benefit). Also gates on max_uses_per_turn.
  // a modifier that's been consumed this turn doesn't apply again
  // until usesThisTurn is reset at end of turn.
  costModifierTotal(player, candidateCard = null, destinationCell = null) {
    let total = 0;
    for (const entry of this.entries) {
      if (entry.effect.op !== OP.MODIFY_CAST_COST) continue;
      if (entry.targetPlayer !== player) continue;
      if (!matchesCostFilter(entry.effect.args?.filter, candidateCard, destinationCell)) continue;
      const max = entry.effect.args?.max_uses_per_turn;
      if (typeof max === 'number' && (entry.usesThisTurn || 0) >= max) continue;
      total += entry.effect.args?.delta || 0;
    }
    return total;
  }

  // v1.111. Like costModifierTotal but skips entries whose filter
  // pins a `cells` list (Harbinger-style cell-restricted modifiers).
  // Used by the hand-card highlight pipeline: the gold "Templar"
  // discount glow should fire ONLY for cards that get a discount
  // regardless of destination cell. Harbinger gets its own purple
  // glow handled separately because its discount applies only on
  // specific squares.
  nonCellDiscountTotal(player, candidateCard = null) {
    let total = 0;
    for (const entry of this.entries) {
      if (entry.effect.op !== OP.MODIFY_CAST_COST) continue;
      if (entry.targetPlayer !== player) continue;
      const filter = entry.effect.args?.filter;
      if (Array.isArray(filter?.cells) && filter.cells.length > 0) continue;
      if (!matchesCostFilter(filter, candidateCard, null)) continue;
      const max = entry.effect.args?.max_uses_per_turn;
      if (typeof max === 'number' && (entry.usesThisTurn || 0) >= max) continue;
      total += entry.effect.args?.delta || 0;
    }
    return total;
  }

  // v1.111. Total discount available from cell-restricted modifiers
  // (Harbinger). Returns the best-case sum when destinationCell is
  // null, or the actual sum when a cell is provided. Used by the
  // purple-glow pipeline to decide which Minion cards are playable
  // ONLY thanks to a Harbinger mark.
  cellRestrictedDiscountTotal(player, candidateCard = null, destinationCell = null) {
    let total = 0;
    for (const entry of this.entries) {
      if (entry.effect.op !== OP.MODIFY_CAST_COST) continue;
      if (entry.targetPlayer !== player) continue;
      const filter = entry.effect.args?.filter;
      if (!Array.isArray(filter?.cells) || filter.cells.length === 0) continue;
      if (!matchesCostFilter(filter, candidateCard, destinationCell)) continue;
      const max = entry.effect.args?.max_uses_per_turn;
      if (typeof max === 'number' && (entry.usesThisTurn || 0) >= max) continue;
      // set_to entries (Caelestis "costs (0)") report their best-case
      // saving against the candidate's printed cost so the glow /
      // mark-restriction pipeline treats them like any discount.
      const setTo = entry.effect.args?.set_to;
      if (typeof setTo === 'number') {
        const printed = typeof candidateCard?.cost === 'number'
          ? candidateCard.cost
          : (candidateCard?.guardian?.cost ?? 0);
        total += Math.min(0, setTo - printed);
        continue;
      }
      total += entry.effect.args?.delta || 0;
    }
    return total;
  }

  // Does the cell-restricted discount that makes this card playable
  // come from a "dragon portal" source (Dragonlord's Lair, Caelestis)
  // rather than a Harbinger mark? Both register the same cell-pinned
  // MODIFY_CAST_COST shape, so the hand-glow pipeline needs the same
  // `filter.subtype === 'dragon'` fork the portal renderer uses
  // (harbingerCeremony.js) to colour the aura teal instead of purple.
  cellRestrictedDiscountKind(player, candidateCard = null, destinationCell = null) {
    let kind = 'harbinger';
    for (const entry of this.entries) {
      if (entry.effect.op !== OP.MODIFY_CAST_COST) continue;
      if (entry.targetPlayer !== player) continue;
      const filter = entry.effect.args?.filter;
      if (!Array.isArray(filter?.cells) || filter.cells.length === 0) continue;
      if (!matchesCostFilter(filter, candidateCard, destinationCell)) continue;
      const max = entry.effect.args?.max_uses_per_turn;
      if (typeof max === 'number' && (entry.usesThisTurn || 0) >= max) continue;
      if (String(filter?.subtype || '').toLowerCase() === 'dragon') return 'dragon';
      if (entry.sourceCardName === 'Town') kind = 'town';
    }
    return kind;
  }

  cellRestrictedDiscountIsDragon(...args) {
    return this.cellRestrictedDiscountKind(...args) === 'dragon';
  }

  // "costs (0)"-style absolute override. Returns the LOWEST matching
  // set_to value, or null when no set_to entry matches. Applied after
  // delta summation: final = min(set_to, base + deltas). Caelestis:
  // "The next Dragon you cast to this location costs (0) this turn."
  costSetTo(player, candidateCard = null, destinationCell = null) {
    let best = null;
    for (const entry of this.entries) {
      if (entry.effect.op !== OP.MODIFY_CAST_COST) continue;
      if (entry.targetPlayer !== player) continue;
      const args = entry.effect.args || {};
      if (typeof args.set_to !== 'number') continue;
      if (!matchesCostFilter(args.filter, candidateCard, destinationCell)) continue;
      const max = args.max_uses_per_turn;
      if (typeof max === 'number' && (entry.usesThisTurn || 0) >= max) continue;
      best = best === null ? args.set_to : Math.min(best, args.set_to);
    }
    return best;
  }

  // The ONE formula for a cast's final mana: flat deltas sum onto the
  // base (which already includes any effect-local delta, e.g. Cradle
  // of Etherrum's cast-from-top discount), set_to overrides win when
  // cheaper, floor at 0. `discounted` mirrors payCastingCost's
  // historical consume gate so per-turn-capped modifiers burn a use
  // only when they actually changed the price.
  castManaBreakdown(player, baseMana, candidateCard = null, destinationCell = null) {
    const adjustment = this.costModifierTotal(player, candidateCard, destinationCell);
    const setTo = this.costSetTo(player, candidateCard, destinationCell);
    let need = Math.max(0, baseMana + adjustment);
    if (setTo !== null) need = Math.min(need, setTo);
    const discounted = adjustment < 0 || setTo !== null || (adjustment !== 0 && need === 0);
    return { need, adjustment, setTo, discounted };
  }

  // v1.99. Mark every matching cost-modifier as consumed this turn.
  // Called from _chargeManaCost after the discount actually applied,
  // so per-turn caps decrement only when the discount fired. All
  // matching capped entries contributed to the final cost (deltas sum;
  // set_to takes the min), so all of them burn a use.
  consumeCostModifierUse(player, candidateCard, destinationCell = null) {
    for (const entry of this.entries) {
      if (entry.effect.op !== OP.MODIFY_CAST_COST) continue;
      if (entry.targetPlayer !== player) continue;
      if (!matchesCostFilter(entry.effect.args?.filter, candidateCard, destinationCell)) continue;
      const max = entry.effect.args?.max_uses_per_turn;
      if (typeof max !== 'number') continue;
      if ((entry.usesThisTurn || 0) >= max) continue;
      entry.usesThisTurn = (entry.usesThisTurn || 0) + 1;
    }
  }

  // v1.99. Reset per-turn usage counters on every cost-modifier
  // entry. Called from _stepPruneExpiredEffects at end of turn so
  // "first per turn" gates rearm cleanly for the next turn.
  resetCostModifierUses() {
    for (const entry of this.entries) {
      if (entry.effect.op !== OP.MODIFY_CAST_COST) continue;
      if (entry.usesThisTurn) entry.usesThisTurn = 0;
    }
  }

  manaContribution(player) {
    let total = 0;
    for (const entry of this.entries) {
      if (entry.controller !== player) continue;
      if (entry.effect.op !== OP.GRANT_MANA) continue;
      total += entry.effect.args.amount || 0;
    }
    return total;
  }

  serialize() {
    return { entries: this.entries.map((e) => ({ ...e })) };
  }

  static deserialize(snapshot) {
    const ledger = new EffectLedger();
    ledger.entries = (snapshot?.entries || []).map((e) => ({ ...e }));
    return ledger;
  }
}
